import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Search, Wallet, ArrowUpCircle, Clock, CheckCircle, AlertCircle, User, Building, FileText, XCircle, Loader } from 'lucide-react';
import { Sidebar } from '../../components/shared/Sidebar';
import { DashboardHeader } from '../../components/shared/DashboardHeader';
import { EscrowStatusTracker } from '../../components/escrow/EscrowStatusTracker';
import { EscrowTransactionLog } from '../../components/escrow/EscrowTransactionLog';
import { useToast } from '../../hooks/useToast';
import { formatCurrency } from '../../utils/calculations';
type EscrowStatus = 'pending' | 'funded' | 'held' | 'released' | 'refunded' | 'disputed';
interface EscrowAccount {
  id: string;
  rentRequestId: string;
  tenant: {
    id: string;
    name: string;
    email: string;
  };
  landlord: {
    id: string;
    name: string;
    email: string;
  };
  property: {
    address: string;
    city: string;
  };
  amount: number;
  status: EscrowStatus;
  fundedAt?: string;
  releasedAt?: string;
  documents: {
    tenancyAgreement: boolean;
    financingAgreement: boolean;
    paymentAuthorization: boolean;
  };
  transactions: {
    id: string;
    type: 'funding' | 'release' | 'refund' | 'hold' | 'dispute';
    amount: number;
    timestamp: string;
    performedBy: string;
    notes?: string;
  }[];
  createdAt: string;
}
// Mock data
const MOCK_ESCROWS: EscrowAccount[] = [{
  id: 'esc1',
  rentRequestId: 'rr1',
  tenant: {
    id: 't1',
    name: 'Adebayo Johnson',
    email: 'adebayo@example.com'
  },
  landlord: {
    id: 'l1',
    name: 'Tunde Bakare',
    email: 'tunde@example.com'
  },
  property: {
    address: '12 Admiralty Way, Lekki Phase 1',
    city: 'Lagos'
  },
  amount: 2400000,
  status: 'funded',
  fundedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
  documents: {
    tenancyAgreement: true,
    financingAgreement: true,
    paymentAuthorization: true
  },
  transactions: [{
    id: 'tx1',
    type: 'funding',
    amount: 2400000,
    timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    performedBy: 'System',
    notes: 'Automatic funding from approved rent request'
  }],
  createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()
}, {
  id: 'esc2',
  rentRequestId: 'rr2',
  tenant: {
    id: 't2',
    name: 'Chioma Okafor',
    email: 'chioma@example.com'
  },
  landlord: {
    id: 'l2',
    name: 'Amina Yusuf',
    email: 'amina@example.com'
  },
  property: {
    address: '45 Gwarinpa Estate',
    city: 'Abuja'
  },
  amount: 3600000,
  status: 'held',
  fundedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
  documents: {
    tenancyAgreement: true,
    financingAgreement: false,
    paymentAuthorization: false
  },
  transactions: [{
    id: 'tx2',
    type: 'funding',
    amount: 3600000,
    timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    performedBy: 'System'
  }, {
    id: 'tx3',
    type: 'hold',
    amount: 3600000,
    timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    performedBy: 'Admin',
    notes: 'Awaiting document signatures from tenant'
  }],
  createdAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString()
}, {
  id: 'esc3',
  rentRequestId: 'rr3',
  tenant: {
    id: 't3',
    name: 'Emeka Nwosu',
    email: 'emeka@example.com'
  },
  landlord: {
    id: 'l1',
    name: 'Tunde Bakare',
    email: 'tunde@example.com'
  },
  property: {
    address: '8 Victoria Arobieke Street',
    city: 'Lagos'
  },
  amount: 1200000,
  status: 'released',
  fundedAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
  releasedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
  documents: {
    tenancyAgreement: true,
    financingAgreement: true,
    paymentAuthorization: true
  },
  transactions: [{
    id: 'tx4',
    type: 'funding',
    amount: 1200000,
    timestamp: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    performedBy: 'System'
  }, {
    id: 'tx5',
    type: 'release',
    amount: 1200000,
    timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    performedBy: 'Admin',
    notes: 'All documents signed. Released to landlord.'
  }],
  createdAt: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString()
}, {
  id: 'esc4',
  rentRequestId: 'rr4',
  tenant: {
    id: 't4',
    name: 'Fatima Ibrahim',
    email: 'fatima@example.com'
  },
  landlord: {
    id: 'l3',
    name: 'Chidi Okonkwo',
    email: 'chidi@example.com'
  },
  property: {
    address: '22 Banana Island Road',
    city: 'Lagos'
  },
  amount: 12000000,
  status: 'pending',
  documents: {
    tenancyAgreement: false,
    financingAgreement: false,
    paymentAuthorization: false
  },
  transactions: [],
  createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString()
}, {
  id: 'esc5',
  rentRequestId: 'rr5',
  tenant: {
    id: 't5',
    name: 'Oluwaseun Adeleke',
    email: 'seun@example.com'
  },
  landlord: {
    id: 'l2',
    name: 'Amina Yusuf',
    email: 'amina@example.com'
  },
  property: {
    address: '15 Wuse Zone 5',
    city: 'Abuja'
  },
  amount: 1800000,
  status: 'refunded',
  fundedAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
  documents: {
    tenancyAgreement: false,
    financingAgreement: false,
    paymentAuthorization: false
  },
  transactions: [{
    id: 'tx6',
    type: 'funding',
    amount: 1800000,
    timestamp: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    performedBy: 'System'
  }, {
    id: 'tx7',
    type: 'refund',
    amount: 1800000,
    timestamp: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
    performedBy: 'Admin',
    notes: 'Signing deadline exceeded. Refunded to Renta.'
  }],
  createdAt: new Date(Date.now() - 16 * 24 * 60 * 60 * 1000).toISOString()
}];
export function EscrowManagement() {
  const {
    toast
  } = useToast();
  const [escrows, setEscrows] = useState(MOCK_ESCROWS);
  const [selectedEscrow, setSelectedEscrow] = useState<EscrowAccount | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [actionNotes, setActionNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState<'release' | 'refund' | null>(null);
  const filteredEscrows = escrows.filter(e => {
    const matchesStatus = statusFilter === 'all' || e.status === statusFilter;
    const matchesSearch = e.tenant.name.toLowerCase().includes(searchQuery.toLowerCase()) || e.landlord.name.toLowerCase().includes(searchQuery.toLowerCase()) || e.property.address.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });
  const allDocsSigned = selectedEscrow ? Object.values(selectedEscrow.documents).every(Boolean) : false;
  const handleRelease = async () => {
    if (!selectedEscrow) return;
    setIsProcessing(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setEscrows(prev => prev.map(e => e.id === selectedEscrow.id ? {
      ...e,
      status: 'released' as const,
      releasedAt: new Date().toISOString(),
      transactions: [...e.transactions, {
        id: `tx-${Date.now()}`,
        type: 'release' as const,
        amount: e.amount,
        timestamp: new Date().toISOString(),
        performedBy: 'Admin',
        notes: actionNotes || 'Released to landlord'
      }]
    } : e));
    setSelectedEscrow(null);
    setActionNotes('');
    setShowConfirmModal(null);
    setIsProcessing(false);
    toast.success(`₦${selectedEscrow.amount.toLocaleString()} released to ${selectedEscrow.landlord.name}`);
  };
  const handleHold = async () => {
    if (!selectedEscrow || !actionNotes.trim()) {
      toast.error('Please provide a reason for holding the escrow');
      return;
    }
    setIsProcessing(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setEscrows(prev => prev.map(e => e.id === selectedEscrow.id ? {
      ...e,
      status: 'held' as const,
      transactions: [...e.transactions, {
        id: `tx-${Date.now()}`,
        type: 'hold' as const,
        amount: e.amount,
        timestamp: new Date().toISOString(),
        performedBy: 'Admin',
        notes: actionNotes
      }]
    } : e));
    setSelectedEscrow(null);
    setActionNotes('');
    setIsProcessing(false);
    toast.success('Escrow placed on hold');
  };
  const handleRefund = async () => {
    if (!selectedEscrow || !actionNotes.trim()) {
      toast.error('Please provide a reason for the refund');
      return;
    }
    setIsProcessing(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setEscrows(prev => prev.map(e => e.id === selectedEscrow.id ? {
      ...e,
      status: 'refunded' as const,
      transactions: [...e.transactions, {
        id: `tx-${Date.now()}`,
        type: 'refund' as const,
        amount: e.amount,
        timestamp: new Date().toISOString(),
        performedBy: 'Admin',
        notes: actionNotes
      }]
    } : e));
    setSelectedEscrow(null);
    setActionNotes('');
    setShowConfirmModal(null);
    setIsProcessing(false);
    toast.success('Escrow refunded to Renta');
  };
  const getStatusBadge = (status: EscrowStatus) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      funded: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      held: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      released: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      refunded: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
      disputed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
    };
    return <span className={`px-2 py-1 rounded-full text-xs font-bold capitalize ${styles[status]}`} data-id="element-2910">
        {status}
      </span>;
  };
  const getDaysHeld = (fundedAt?: string) => {
    if (!fundedAt) return 0;
    const funded = new Date(fundedAt);
    const now = new Date();
    return Math.floor((now.getTime() - funded.getTime()) / (1000 * 60 * 60 * 24));
  };
  // Stats
  const totalHeld = escrows.filter(e => e.status === 'funded' || e.status === 'held').reduce((sum, e) => sum + e.amount, 0);
  const pendingRelease = escrows.filter(e => e.status === 'funded').length;
  const releasedToday = escrows.filter(e => e.status === 'released' && e.releasedAt && new Date(e.releasedAt).toDateString() === new Date().toDateString()).length;
  return <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900 pb-16 lg:pb-0" data-id="element-2911">
      <Sidebar data-id="element-2912" />

      <main className="flex-1 lg:ml-[240px]" data-id="element-2913">
        <DashboardHeader data-id="element-2914" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-2915">
          {/* Header */}
          <div className="mb-8" data-id="element-2916">
            <div className="flex items-center justify-between mb-4" data-id="element-2917">
              <div className="flex items-center space-x-3" data-id="element-2918">
                <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900 rounded-xl flex items-center justify-center" data-id="element-2919">
                  <Shield className="h-6 w-6 text-blue-600 dark:text-blue-400" data-id="element-2920" />
                </div>
                <div data-id="element-2921">
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-2922">
                    Escrow Management
                  </h1>
                  <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2923">
                    Monitor and manage escrow accounts
                  </p>
                </div>
              </div>

              {/* Stats */}
              <div className="hidden md:flex items-center space-x-6" data-id="element-2924">
                <div className="text-center" data-id="element-2925">
                  <div className="text-xl font-bold text-blue-600 dark:text-blue-400" data-id="element-2926">
                    {formatCurrency(totalHeld)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2927">
                    Total Held
                  </div>
                </div>
                <div className="text-center" data-id="element-2928">
                  <div className="text-xl font-bold text-yellow-600 dark:text-yellow-400" data-id="element-2929">
                    {pendingRelease}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2930">
                    Pending Release
                  </div>
                </div>
                <div className="text-center" data-id="element-2931">
                  <div className="text-xl font-bold text-green-600 dark:text-green-400" data-id="element-2932">
                    {releasedToday}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2933">
                    Released Today
                  </div>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-2934">
              <div className="relative flex-1" data-id="element-2935">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" data-id="element-2936" />
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search by tenant, landlord, or property..." className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-2937" />
              </div>

              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-2938">
                <option value="all" data-id="element-2939">All Status</option>
                <option value="pending" data-id="element-2940">Pending</option>
                <option value="funded" data-id="element-2941">Funded</option>
                <option value="held" data-id="element-2942">Held</option>
                <option value="released" data-id="element-2943">Released</option>
                <option value="refunded" data-id="element-2944">Refunded</option>
              </select>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2945">
            {/* Escrow List */}
            <div className="space-y-4 max-h-[calc(100vh-280px)] overflow-y-auto pr-2" data-id="element-2946">
              {filteredEscrows.length > 0 ? filteredEscrows.map((escrow, index) => <motion.div key={escrow.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.05
            }} onClick={() => {
              setSelectedEscrow(escrow);
              setActionNotes('');
            }} className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border-2 p-4 cursor-pointer transition-all hover:shadow-md ${selectedEscrow?.id === escrow.id ? 'border-blue-500 dark:border-blue-400' : 'border-gray-200 dark:border-gray-700'}`} data-id="element-2947">
                    <div className="flex items-start justify-between mb-3" data-id="element-2948">
                      <div data-id="element-2949">
                        <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-2950">
                          {escrow.tenant.name}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2951">
                          {escrow.property.address}
                        </p>
                      </div>
                      {getStatusBadge(escrow.status)}
                    </div>

                    <div className="flex items-center justify-between" data-id="element-2952">
                      <div data-id="element-2953">
                        <p className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-2954">
                          {formatCurrency(escrow.amount)}
                        </p>
                        {escrow.fundedAt && <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2955">
                            {getDaysHeld(escrow.fundedAt)} days held
                          </p>}
                      </div>
                      <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-2956">
                        <Wallet className="h-3 w-3" data-id="element-2957" />
                        <span data-id="element-2958">{escrow.transactions.length} transactions</span>
                      </div>
                    </div>
                  </motion.div>) : <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center" data-id="element-2959">
                  <Shield className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" data-id="element-2960" />
                  <p className="text-gray-500 dark:text-gray-400" data-id="element-2961">
                    No escrow accounts found
                  </p>
                </div>}
            </div>

            {/* Detail Panel */}
            <div className="lg:sticky lg:top-24 lg:h-[calc(100vh-280px)]" data-id="element-2962">
              <AnimatePresence mode="wait" data-id="element-2963">
                {selectedEscrow ? <motion.div key={selectedEscrow.id} initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 h-full overflow-y-auto" data-id="element-2964">
                    {/* Header */}
                    <div className="p-6 border-b border-gray-200 dark:border-gray-700" data-id="element-2965">
                      <div className="flex items-start justify-between mb-4" data-id="element-2966">
                        <div data-id="element-2967">
                          <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-2968">
                            {formatCurrency(selectedEscrow.amount)}
                          </h2>
                          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2969">
                            {selectedEscrow.property.address},{' '}
                            {selectedEscrow.property.city}
                          </p>
                        </div>
                        {getStatusBadge(selectedEscrow.status)}
                      </div>

                      {/* Parties */}
                      <div className="grid grid-cols-2 gap-4" data-id="element-2970">
                        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg" data-id="element-2971">
                          <p className="text-xs text-blue-600 dark:text-blue-400 font-medium mb-1" data-id="element-2972">
                            Tenant
                          </p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2973">
                            {selectedEscrow.tenant.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2974">
                            {selectedEscrow.tenant.email}
                          </p>
                        </div>
                        <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg" data-id="element-2975">
                          <p className="text-xs text-green-600 dark:text-green-400 font-medium mb-1" data-id="element-2976">
                            Landlord
                          </p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2977">
                            {selectedEscrow.landlord.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2978">
                            {selectedEscrow.landlord.email}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-6 space-y-6" data-id="element-2979">
                      {/* Status Tracker */}
                      <div data-id="element-2980">
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-4" data-id="element-2981">
                          Escrow Status
                        </h3>
                        <EscrowStatusTracker currentStatus={selectedEscrow.status} steps={[{
                      status: 'pending',
                      label: 'Pending',
                      date: selectedEscrow.createdAt
                    }, {
                      status: 'funded',
                      label: 'Funded',
                      date: selectedEscrow.fundedAt
                    }, {
                      status: 'held',
                      label: 'Held',
                      date: selectedEscrow.transactions.find(t => t.type === 'hold')?.timestamp
                    }, {
                      status: 'released',
                      label: 'Released',
                      date: selectedEscrow.releasedAt
                    }]} data-id="element-2982" />
                      </div>

                      {/* Documents Status */}
                      <div data-id="element-2983">
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center" data-id="element-2984">
                          <FileText className="h-5 w-5 mr-2 text-blue-600" data-id="element-2985" />
                          Document Status
                        </h3>
                        <div className="space-y-2" data-id="element-2986">
                          {[{
                        key: 'tenancyAgreement',
                        label: 'Tenancy Agreement'
                      }, {
                        key: 'financingAgreement',
                        label: 'Financing Agreement'
                      }, {
                        key: 'paymentAuthorization',
                        label: 'Payment Authorization'
                      }].map(doc => <div key={doc.key} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg" data-id="element-2987">
                              <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-2988">
                                {doc.label}
                              </span>
                              {selectedEscrow.documents[doc.key as keyof typeof selectedEscrow.documents] ? <span className="flex items-center text-green-600 dark:text-green-400 text-xs font-medium" data-id="element-2989">
                                  <CheckCircle className="h-4 w-4 mr-1" data-id="element-2990" />
                                  Signed
                                </span> : <span className="flex items-center text-yellow-600 dark:text-yellow-400 text-xs font-medium" data-id="element-2991">
                                  <Clock className="h-4 w-4 mr-1" data-id="element-2992" />
                                  Pending
                                </span>}
                            </div>)}
                        </div>
                        {allDocsSigned && <div className="mt-3 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg" data-id="element-2993">
                            <p className="text-sm text-green-700 dark:text-green-400 flex items-center" data-id="element-2994">
                              <CheckCircle className="h-4 w-4 mr-2" data-id="element-2995" />
                              All documents signed - Ready for release
                            </p>
                          </div>}
                      </div>

                      {/* Transaction Log */}
                      <div data-id="element-2996">
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center" data-id="element-2997">
                          <Wallet className="h-5 w-5 mr-2 text-blue-600" data-id="element-2998" />
                          Transaction History
                        </h3>
                        <EscrowTransactionLog transactions={selectedEscrow.transactions} data-id="element-2999" />
                      </div>

                      {/* Admin Actions */}
                      {(selectedEscrow.status === 'funded' || selectedEscrow.status === 'held') && <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-3000">
                          <div data-id="element-3001">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3002">
                              Action Notes
                            </label>
                            <textarea value={actionNotes} onChange={e => setActionNotes(e.target.value)} rows={2} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" placeholder="Add notes for this action..." data-id="element-3003" />
                          </div>

                          <div className="flex flex-col sm:flex-row gap-3" data-id="element-3004">
                            {selectedEscrow.status === 'funded' && allDocsSigned && <button onClick={() => setShowConfirmModal('release')} disabled={isProcessing} className="flex-1 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition-colors flex items-center justify-center disabled:opacity-50" data-id="element-3005">
                                  <ArrowUpCircle className="h-5 w-5 mr-2" data-id="element-3006" />
                                  Release to Landlord
                                </button>}

                            {selectedEscrow.status === 'funded' && !allDocsSigned && <button onClick={handleHold} disabled={isProcessing} className="flex-1 py-3 bg-orange-600 text-white rounded-lg font-bold hover:bg-orange-700 transition-colors flex items-center justify-center disabled:opacity-50" data-id="element-3007">
                                  {isProcessing ? <Loader className="h-5 w-5 animate-spin" data-id="element-3008" /> : <>
                                      <AlertCircle className="h-5 w-5 mr-2" data-id="element-3009" />
                                      Hold Escrow
                                    </>}
                                </button>}

                            {selectedEscrow.status === 'held' && <>
                                <button onClick={() => setShowConfirmModal('release')} disabled={isProcessing || !allDocsSigned} className="flex-1 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition-colors flex items-center justify-center disabled:opacity-50" data-id="element-3010">
                                  <ArrowUpCircle className="h-5 w-5 mr-2" data-id="element-3011" />
                                  Release
                                </button>
                                <button onClick={() => setShowConfirmModal('refund')} disabled={isProcessing} className="flex-1 py-3 bg-gray-600 text-white rounded-lg font-bold hover:bg-gray-700 transition-colors flex items-center justify-center disabled:opacity-50" data-id="element-3012">
                                  <XCircle className="h-5 w-5 mr-2" data-id="element-3013" />
                                  Refund to Renta
                                </button>
                              </>}
                          </div>
                        </div>}

                      {/* Completed Status */}
                      {(selectedEscrow.status === 'released' || selectedEscrow.status === 'refunded') && <div className={`p-4 rounded-lg ${selectedEscrow.status === 'released' ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' : 'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'}`} data-id="element-3014">
                          <div className="flex items-center space-x-2" data-id="element-3015">
                            {selectedEscrow.status === 'released' ? <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" data-id="element-3016" /> : <XCircle className="h-5 w-5 text-gray-600 dark:text-gray-400" data-id="element-3017" />}
                            <span className={`font-semibold ${selectedEscrow.status === 'released' ? 'text-green-700 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'}`} data-id="element-3018">
                              Escrow{' '}
                              {selectedEscrow.status === 'released' ? 'Released' : 'Refunded'}
                            </span>
                          </div>
                          {selectedEscrow.releasedAt && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1" data-id="element-3019">
                              {new Date(selectedEscrow.releasedAt).toLocaleDateString('en-NG', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                            </p>}
                        </div>}
                    </div>
                  </motion.div> : <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center h-full flex flex-col items-center justify-center" data-id="element-3020">
                    <Shield className="h-16 w-16 text-gray-300 dark:text-gray-600 mb-4" data-id="element-3021" />
                    <p className="text-gray-500 dark:text-gray-400" data-id="element-3022">
                      Select an escrow account to view details
                    </p>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Confirmation Modal */}
        <AnimatePresence data-id="element-3023">
          {showConfirmModal && <>
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onClick={() => setShowConfirmModal(null)} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" data-id="element-3024" />
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-3025">
                <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.95
            }} className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6" data-id="element-3026">
                  <div className={`h-12 w-12 rounded-full flex items-center justify-center mx-auto mb-4 ${showConfirmModal === 'release' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700'}`} data-id="element-3027">
                    {showConfirmModal === 'release' ? <ArrowUpCircle className="h-6 w-6 text-green-600 dark:text-green-400" data-id="element-3028" /> : <XCircle className="h-6 w-6 text-gray-600 dark:text-gray-400" data-id="element-3029" />}
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2" data-id="element-3030">
                    {showConfirmModal === 'release' ? 'Release Escrow?' : 'Refund Escrow?'}
                  </h3>

                  <p className="text-gray-600 dark:text-gray-400 text-center mb-6" data-id="element-3031">
                    {showConfirmModal === 'release' ? `This will release ${formatCurrency(selectedEscrow?.amount || 0)} to ${selectedEscrow?.landlord.name}. This action cannot be undone.` : `This will refund ${formatCurrency(selectedEscrow?.amount || 0)} to Renta. This action cannot be undone.`}
                  </p>

                  {showConfirmModal === 'refund' && !actionNotes.trim() && <div className="mb-4" data-id="element-3032">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3033">
                        Refund Reason (Required)
                      </label>
                      <textarea value={actionNotes} onChange={e => setActionNotes(e.target.value)} rows={2} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" placeholder="Explain why this escrow is being refunded..." data-id="element-3034" />
                    </div>}

                  <div className="flex gap-3" data-id="element-3035">
                    <button onClick={() => setShowConfirmModal(null)} disabled={isProcessing} className="flex-1 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" data-id="element-3036">
                      Cancel
                    </button>
                    <button onClick={showConfirmModal === 'release' ? handleRelease : handleRefund} disabled={isProcessing || showConfirmModal === 'refund' && !actionNotes.trim()} className={`flex-1 py-3 rounded-lg font-bold transition-colors flex items-center justify-center disabled:opacity-50 ${showConfirmModal === 'release' ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-600 text-white hover:bg-gray-700'}`} data-id="element-3037">
                      {isProcessing ? <Loader className="h-5 w-5 animate-spin" data-id="element-3038" /> : showConfirmModal === 'release' ? 'Release Funds' : 'Confirm Refund'}
                    </button>
                  </div>
                </motion.div>
              </div>
            </>}
        </AnimatePresence>
      </main>
    </div>;
}